import type { components } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { Button, Field, Input, Select } from "@brandex/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { useState } from "react";

import { QueryError, fieldError } from "../views";

type Publication = components["schemas"]["PublicationDto"];
export type Platform = Publication["platform"];
export type Account = Publication["account"];

export const PLATFORMS: readonly { value: Platform; label: string }[] = [
  { value: "website", label: "Doctor website" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "youtube", label: "YouTube" },
  { value: "tiktok", label: "TikTok" },
  { value: "linkedin", label: "LinkedIn" },
];
const PLATFORM_LABEL = Object.fromEntries(PLATFORMS.map((p) => [p.value, p.label])) as Record<Platform, string>;

/** "Doctors Diary · Facebook" or "Doctor's own · Facebook" (the website is always the doctor's). */
export function channelLabel(platform: Platform, account: Account): string {
  if (platform === "website") return PLATFORM_LABEL.website;
  return `${account === "doctors_diary" ? "Doctors Diary" : "Doctor's own"} · ${PLATFORM_LABEL[platform]}`;
}

/**
 * Logs where an approved item went live (F-12). The first entry moves the
 * item to Published, and the doctor sees the link in Content and approvals.
 * Used on the pipeline board's cards; the Publishing log has the same form
 * with an item picker.
 */
export function PublishForm({ item, onDone, onCancel }: { item: { id: string; title: string }; onDone: () => void; onCancel?: () => void }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [platform, setPlatform] = useState<Platform>("facebook");
  const [account, setAccount] = useState<Account>("doctors_diary");
  const [url, setUrl] = useState("");
  const save = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/publications", {
        body: { deliverable_id: item.id, platform, account: platform === "website" ? "doctor" : account, url: url.trim(), published_at: null },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "pipeline"] });
      void qc.invalidateQueries({ queryKey: ["admin", "publications"] });
      onDone();
    },
  });
  const ready = url.trim().startsWith("https://");
  return (
    <form aria-label={`Publish ${item.title}`} className="grid gap-2" onSubmit={(e) => { e.preventDefault(); if (ready) save.mutate(); }}>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Platform">
          <Select value={platform} onChange={(e) => setPlatform(e.target.value as Platform)}>
            {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
        </Field>
        <Field label="Account">
          <Select value={platform === "website" ? "doctor" : account} disabled={platform === "website"} onChange={(e) => setAccount(e.target.value as Account)}>
            <option value="doctors_diary">Doctors Diary</option>
            <option value="doctor">Doctor's own</option>
          </Select>
        </Field>
      </div>
      <Field label="Post link" hint="The live post, e.g. https://facebook.com/…" error={fieldError(save.error, "url")}>
        <Input type="url" placeholder="https://" value={url} autoFocus onChange={(e) => setUrl(e.target.value)} />
      </Field>
      {save.isError && fieldError(save.error, "url") === undefined ? <QueryError error={save.error} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={!ready} loading={save.isPending}>
          <Send className="h-3.5 w-3.5" aria-hidden />
          Mark as published
        </Button>
        {onCancel ? <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button> : null}
      </div>
    </form>
  );
}
